import { useState, useEffect } from "react";
import { AppRoute } from "@/App";
import { useNavigate } from "react-router-dom";
import chatRepository from "@/services/chatRepository";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { userInfoRepository } from "@/services/userInfoRepository";

export interface ChatScreenViewModel {
  messages: ChatMessageUi[];
  currentMessage: string;
  isLoading: boolean;
  error: string | null;
  isEnabledInput: boolean;
  availableQuestionsCount: number;
  hasSubscription: boolean;
  skipUp: number;
  skipDown: number;
  astrologistName: string;
  astrologistAvatar: string;
  astrologistDescription: string;
  fetchMessages: (direction: FetchDirection) => void;
  chatLoading: { up: boolean; down: boolean };
  // Методы управления сообщениями
  sendUserMessage: () => void;
  setCurrentMessage: (message: string) => void;

  // Методы навигации
  navigateToHome: () => void;
  navigateToSubscription: () => void;
  isCenterSheetOpen: boolean;
  closeCenterSheet: () => void;
}

export enum FetchDirection {
  Up = "up",
  Down = "down",
}

export const useChatScreenViewModel = (): ChatScreenViewModel => {
  const [isEnabledInput, setIsEnabledInput] = useState(false);
  const [astrologistName, setAstrologistName] = useState<string>("");
  const [astrologistAvatar, setAstrologistAvatar] = useState<string>("");
  const [astrologistDescription, setAstrologistDescription] =
    useState<string>("");
  const [chatId, setChatId] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessageUi[]>([]);
  const [skipUp, setSkipUp] = useState(0);
  const [skipDown, setSkipDown] = useState(0);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState({ up: false, down: false });
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [availableQuestionsCount, setAvailableQuestionsCount] =
    useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasMoreUp, setHasMoreUp] = useState(true);
  const [hasMoreDown, setHasMoreDown] = useState(true);
  const [isCenterSheetOpen, setIsCenterSheetOpen] = useState(false);
  const {
    getChatIdByUserId,
    getMessages,
    sendMessage,
    canUserSendMessage,
    getAvailableQuestionsCount,
  } = chatRepository;

  const { getUserInfo, getConfig } = userInfoRepository;

  const { subscription, isLoading: subscriptionLoading } = useSubscription();

  useEffect(() => {
    initScreen();
  }, [subscription, subscriptionLoading]);

  const fetchMessages = async (direction: FetchDirection) => {
    if (!chatId || !isInitialized) return;
    if (chatLoading.up || chatLoading.down) return;
    if (direction === "up" && !hasMoreUp) return;
    if (direction === "down" && !hasMoreDown) return;

    setChatLoading((prev) => ({ ...prev, [direction]: true }));
    try {
      let messages: ChatMessageUi[] = [];
      let skip = 0;

      if (direction === "up") {
        skip = Math.max(0, skipUp - 1);
        const loaded = await getMessages(chatId, 20, skip);
        if (loaded.length === 0) {
          setHasMoreUp(false);
          return;
        }
        messages = loaded.map((message) => ({
          senderId: message.senderId,
          message: message.message,
          createdAt: message.createdAt,
          isUserMessage: message.senderId === userId,
        }));
        setMessages((prev) => [...messages, ...prev]);
        setSkipUp(skip);
      } else {
        skip = skipDown;
        const loaded = await getMessages(chatId, 20, skip);
        if (loaded.length === 0) {
          setHasMoreDown(false);
          return;
        }
        messages = loaded.map((message) => ({
          senderId: message.senderId,
          message: message.message,
          createdAt: message.createdAt,
          isUserMessage: message.senderId === userId,
        }));
        setMessages((prev) => [...prev, ...messages]);
        setSkipDown(skip + 1);
      }
    } catch (error) {
      setError("Ошибка загрузки сообщений");
    } finally {
      setChatLoading((prev) => ({ ...prev, [direction]: false }));
    }
  };
  
  const getChatId = async () => {
    try {
      return await getChatIdByUserId();
    } catch (error) {
      console.error("Error getting chat id:", error);
      return null;
    }
  };
  const getCanUserSendMessage = async () => {
    try {
      return await canUserSendMessage();
    } catch (error) {
      console.error("Error getting can user send message:", error);
      return false;
    }
  };

  const initScreen = async () => {
    try {
      if (subscriptionLoading) {
        return;
      }
      setIsLoading(true);
      const astrologistInfo = await getConfig();
      setAstrologistName(astrologistInfo?.astrologistName ?? "");
      setAstrologistAvatar(astrologistInfo?.astrologistAvatar ?? "");
      setAstrologistDescription(astrologistInfo?.astrologistDescription ?? "");
      const userInfo = await getUserInfo();

      const hasSubscription = subscription?.isSubscribed ?? false;
      const chatId = await getChatId();

      const canUserSend = await getCanUserSendMessage();

      if (hasSubscription) {
        setIsCenterSheetOpen(!canUserSend);
      }
      setHasSubscription(hasSubscription);

      setIsEnabledInput(canUserSend);

      if (chatId) {

        setChatId(chatId);
        setUserId(userInfo?.id ?? 0);

        const availableQuestionsCount = await getAvailableQuestionsCount(
          chatId
        );
        setAvailableQuestionsCount(availableQuestionsCount);

        await updateMessages(chatId, userInfo?.id ?? 0);
        setIsInitialized(true);
      }
    } catch (error) {
      console.error("Error initializing chat:", error);
      setError("Ошибка инициализации чата");
    } finally {
      setIsLoading(false);
    }
  };

  const sendUserMessage = async () => {
    if (!currentMessage.trim() || !chatId) return;

    try {
      await sendMessage(currentMessage, chatId);
      setMessages((prev) => [
        {
          senderId: userId ?? 0,
          message: currentMessage,
          createdAt: new Date(),
          isUserMessage: true,
        },
        ...prev,
      ]);
      setCurrentMessage("");
      setSkipDown(0);
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Ошибка отправки сообщения");
    } finally {
      setIsLoading(false);
    }
  };

  const updateMessages = async (chatId: string, userId: number) => {
    try {
      const loaded = await getMessages(chatId, 20, 0);
      const messages = loaded.map((message) => ({
        senderId: message.senderId,
        message: message.message,
        createdAt: message.createdAt,
        isUserMessage: message.senderId === userId,
      }));
      setMessages(messages);
      setHasMoreUp(false);
      setHasMoreDown(loaded.length === 20);
      setSkipUp(0);
      setSkipDown(1);
    } catch (error) {
      setError("Ошибка обновления сообщений");
    }
  };

  const navigateToHome = () => {
    navigate(AppRoute.Home);
  };

  const navigateToSubscription = () => {
    navigate(AppRoute.Subscription);
  };

  const closeCenterSheet = () => {
    setIsCenterSheetOpen(false);
  };

  return {
    astrologistName,
    astrologistAvatar,
    astrologistDescription,
    isEnabledInput,
    messages,
    currentMessage,
    isLoading,
    error,
    sendUserMessage,
    setCurrentMessage,
    navigateToHome,
    navigateToSubscription,
    availableQuestionsCount,
    hasSubscription,
    fetchMessages,
    chatLoading,
    skipUp,
    skipDown,
    isCenterSheetOpen,
    closeCenterSheet,
  };
};

export interface ChatMessageUi {
  senderId: number;
  message: string;
  createdAt: Date;
  isUserMessage: boolean;
}
